import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getBookingWidgetMultipropertyData } from 'features/multiproperty/MultipropertySlice';

import {
    COOKIE_NAME,
    WIDGET_LOCAL_STORAGE_KEY,
    WIDGET_STATS_SETTINGS,
} from '../../config';
import {
    areRatesReady,
    getCookie,
    getLanguageCode,
    getLocalStorageData,
    getPricesUrl,
    isMobileDeviceRequest,
    makeStatsRequestThrottled,
} from '../../lib';

import { ratestrip } from '../../interface';

export const getMiniBookingWidgetData = createAsyncThunk(
    'mini-booking-widget/get-data',
    async (params = {}) => {
        const {
            adults,
            checkin,
            checkout,
            localeLanguage,
            currency,
            nights,
            rooms,
            children,
            infants,
            voucher,
            price,
            smartGuest,
            bookingEngine,
            sortBy,
            keepState,
            livePrices,
            propertyId,
            isMultipropertyStill,
            parentPropertyId,
        } = params;

        let href =
            bookingEngine === 1 ? window.location.origin : window.location.href;
        const langCode = getLanguageCode();
        const cookie = getCookie(COOKIE_NAME);
        const mobileRequest = isMobileDeviceRequest();

        const pageTitle = document.title;

        // Construct query string
        if (href.endsWith('#')) {
            href = href.substring(0, href.length - 1);
        }
        let queryString = '';
        queryString =
            process.env.NODE_ENV === 'development' ? '' : `url=${href}`;
        queryString += localeLanguage
            ? `&lang=${localeLanguage}`
            : `&lang=${langCode}`;
        queryString += `&cookie=${cookie}`;
        queryString += `&mobile=${mobileRequest}`;

        queryString += adults !== undefined ? `&widgetAdults=${adults}` : '';
        queryString += checkin !== undefined ? `&widgetCheckin=${checkin}` : '';
        queryString +=
            checkout !== undefined ? `&widgetCheckout=${checkout}` : '';
        queryString +=
            localeLanguage !== undefined
                ? `&localeLanguage=${localeLanguage}`
                : '';
        queryString += currency !== undefined ? `&currency=${currency}` : '';
        queryString += nights !== undefined ? `&nights=${nights}` : '';
        queryString += rooms !== undefined ? `&rooms=${rooms}` : '';
        queryString +=
            children !== undefined ? `&widgetChildren=${children}` : '';
        queryString += infants !== undefined ? `&widgetInfants=${infants}` : '';
        queryString += voucher !== undefined ? `&voucher=${voucher}` : '';
        queryString += price !== undefined ? `&price=${price}` : '';
        queryString +=
            smartGuest !== undefined ? `&smartguest=${smartGuest}` : '';
        queryString +=
            bookingEngine !== undefined
                ? `&bookingEngine=${bookingEngine}`
                : '';
        queryString += sortBy !== undefined ? `&sort=${sortBy}` : '';

        queryString += keepState !== undefined ? `&keepState=${keepState}` : '';

        queryString +=
            livePrices !== undefined ? `&livePrices=${livePrices}` : '';
        queryString +=
            propertyId !== undefined ? `&propertyID=${propertyId}` : '';
        queryString +=
            parentPropertyId !== undefined
                ? `&parentPropertyID=${parentPropertyId}`
                : '';
        queryString +=
            isMultipropertyStill !== undefined
                ? `&isMultipropertyStill=${isMultipropertyStill}`
                : '';
        queryString += pageTitle !== undefined ? `&pageTitle=${pageTitle}` : '';

        const url = getPricesUrl(queryString);

        const response = await axios.get(url);

        // TODO: DON'T KNOW IF THIS IS THE RIGHT PLACE TO EXECUTE THIS CODE
        if (response.status === 200) {
            const propertyId = response?.data?.propertyID;
            makeStatsRequestThrottled(
                WIDGET_STATS_SETTINGS.actions.open,
                propertyId
            );
        }

        // TODO: FIX TYPO
        if (response.data?.rememberClose) {
            const miniBookingWidgetSavedState = getLocalStorageData(
                WIDGET_LOCAL_STORAGE_KEY
            );

            if (
                miniBookingWidgetSavedState &&
                miniBookingWidgetSavedState.isOpen !== undefined
            ) {
                response.data.open = miniBookingWidgetSavedState.isOpen;
            }
        }

        if (response.data?.showMultipropertySmallBtn) {
            const miniBookingWidgetSavedState = getLocalStorageData(
                WIDGET_LOCAL_STORAGE_KEY
            );

            if (
                miniBookingWidgetSavedState &&
                miniBookingWidgetSavedState.isDismissed !== undefined
            ) {
                response.data.isDismissed =
                    miniBookingWidgetSavedState.isDismissed;
            }
        }

        // some browsers don't display prompts created in beforeunload event
        // handlers unless the page has been interacted with
        if (!window.onbeforeunload && response.data?.enableBeforeExitAlert) {
            window.onbeforeunload = function (event) {
                event.preventDefault();

                const confirmationMessage = 'o/';
                (event || window.event).returnValue = confirmationMessage; // Gecko + IE

                return confirmationMessage; /* Safari, Chrome, and other WebKit-derived browsers */
            };
        }

        return response.data;
    }
);

export const miniBookingWidgetSlice = createSlice({
    name: 'miniBookingWidget',
    initialState: {
        show: false,
        data: null,
        isFetching: false,
        isSuccess: false,
        isError: false,
        errorMessage: '',
        hidden: false,
        price: null,
        channels: [],
        ourOffersList: [],
        widgetReady: false,
        notificationsAvailable: false,
        ratesReady: false,
        open: null,
        isDismissed: false,
        multipropertyData: {},
        multipropertyScoreFilter: [],
        openBigWidgetElmSelectors: null,
        showOnlySmallMutlipropertyButton: null,
        showSingleWidgetMultiproperty: null,
        smallMultipropBtnText: '',
        widgetPosition: {},
        preRetars: [],
        bookDirectly: false,
    },
    reducers: {
        toggle: (state) => {
            state.open = !state.open;
        },
        setDismissed: (state, action) => {
            state.isDismissed = action.payload.isDismissed;
        },
        minimize: (state) => {
            state.open = false;
        },
        hide: (state) => {
            state.hidden = true;
        },
        setPrice: (state, action) => {
            state.price = action.payload.price;
        },
        updateChannel: (state, action) => {
            const channelId = action.payload.channelId;
            const price = action.payload.price;
            const channelIndex = state.channels.findIndex(
                (channel) => channel?.channelID === channelId
            );
            if (channelIndex !== -1) {
                // There is no price remove the channel
                // eslint-disable-next-line no-useless-escape
                if (!price || price === 0 || /^0*[\.,]?0*$/.test(price)) {
                    state.channels = state.channels.filter(
                        (channel) => channel?.channelID !== channelId
                    );
                } else {
                    state.channels[channelIndex].price = price;
                }
            }
        },
        updateOurPrice: (state, action) => {
            // TODO: 'ourPrice' will be replaced
            // what should happen if a property is missing?
            state.data.ourPrice = action.payload.ourPrice;
            state.ratesReady = areRatesReady(action.payload?.ourPrice || {});
            state.price = action.payload?.ourPrice?.price || '0';
        },
        resetNotificationsAvailable: (state) => {
            state.notificationsAvailable = false;
        },
        resetAllRoomsUrl: (state) => {
            state.data.bigPlugin.allRoomsUrl = '';
        },
        setCalendar: (state, { payload }) => {
            if (payload.length > 0) {
                state.data.futureDates = payload || {};
            }
        },
        setCartID: (state, { payload }) => {
            if (payload.length > 0) {
                state.data.cartID = payload || '';
            }
        },
    },
    extraReducers: {
        [getMiniBookingWidgetData.fulfilled]: (state, { payload }) => {
            state.multipropertyData = {
                lowestMultiPropertyPrice:
                    payload?.lowestMultiPropertyPrice ||
                    state.multipropertyData.lowestMultiPropertyPrice,
                multiPropertyBestDealText:
                    payload?.multiPropertyBestDealText ||
                    state.multipropertyData.multiPropertyBestDealText,
                multiPropertyCount:
                    payload?.multiPropertyCount ||
                    state.multipropertyData.multiPropertyCount,
                multiPropertyImage:
                    payload?.multiPropertyImage ||
                    state.multipropertyData.multiPropertyImage,
                multipropertyViewAllButton:
                    payload?.multipropertyViewAllButton ||
                    state.multipropertyData.multipropertyViewAllButton,
            };
            state.multipropertyScoreFilter =
                payload.multipropertyScoreFilter || [];

            state.showOnlySmallMutlipropertyButton =
                state.data?.showOnlySmallMutlipropertyButton ||
                payload?.showOnlySmallMutlipropertyButton;

            state.showSingleWidgetMultiproperty =
                state.data?.showSingleWidgetMultiproperty ||
                payload?.showSingleWidgetMultiproperty;

            state.smallMultipropBtnText =
                state.smallMultipropBtnText ||
                payload?.Lang?.smallMultipropBtnText;

            if (!Object.keys(state.widgetPosition).length) {
                state.widgetPosition = payload?.widgetPosition;
            }
            if (!Array.isArray(state.preRetars) || !state.preRetars.length) {
                state.preRetars = payload?.preRetars;
            }

            state.data = payload;
            state.show = true;
            state.isFetching = false;
            state.isSuccess = true;
            state.isError = false;
            state.errorMessage = '';
            if (payload.open === false || payload.open === 'false') {
                state.open = false;
            } else if (payload.open === true || payload.open === 'true') {
                state.open = true;
            }
            state.isDismissed = payload.isDismissed;
            state.hidden = payload.hidden;
            state.bookDirectly = payload.bookDirectly;
            state.price = payload.ourPrice?.price;
            state.channels = payload?.channels || [];
            state.ourOffersList = payload?.ourOffers?.offersList || [];
            state.widgetReady = true;
            state.notificationsAvailable =
                payload.notificationsUrl !== undefined &&
                payload.notificationsUrl !== '';
            ratestrip.widgetReady = true;
            state.ratesReady = areRatesReady(payload?.ourPrice || {});
            state.openBigWidgetElmSelectors = payload.openBigWidgetElmSelectors;
            state.popUpNewList = payload.popUpNewList;
        },
        [getMiniBookingWidgetData.rejected]: (state, { payload }) => {
            state.data = null;
            state.show = false;
            state.isFetching = false;
            state.isSuccess = false;
            state.isError = true;
            state.errorMessage = '';
            state.open = false;
            state.hidden = true;
            state.price = null;
            state.bookDirectly = false;
        },
        [getMiniBookingWidgetData.pending]: (state) => {
            state.isFetching = true;
            state.isSuccess = false;
            state.isError = false;
            state.errorMessage = '';
        },
        [getBookingWidgetMultipropertyData.fulfilled]: (state, { payload }) => {
            state.multipropertyData.lowestMultiPropertyPrice =
                payload.multipropertyOptions?.lowestMultiPropertyPrice;
            state.multipropertyData.multiPropertyBestDealText =
                payload.multipropertyOptions?.multiPropertyBestDealText;
            state.multipropertyData.multiPropertyCount =
                payload.multipropertyOptions?.multiPropertyCount;
            state.multipropertyData.multiPropertyImage =
                payload.multipropertyOptions?.multiPropertyImage;
            state.multipropertyData.multipropertyViewAllButton =
                payload.multipropertyOptions?.multipropertyViewAllButton;
        },
    },
});

export const {
    toggle,
    minimize,
    hide,
    setPrice,
    updateChannel,
    updateOurPrice,
    resetAllRoomsUrl,
    resetNotificationsAvailable,
    setDismissed,
    setCalendar,
    setCartID,
} = miniBookingWidgetSlice.actions;
